/**
 * Autocomplete
 *
 * The search-as-you-type input used across admin: a Semantic search dropdown fed by the caller's
 * own server-side search. Each option shows its text, an optional subtitle and extra lines, and
 * optional right-aligned details. The selected value shows the option's text.
 */

import React, {
  ReactNode,
  Ref,
  useEffect,
  useId,
  useRef,
  useState
} from 'react';
import {
  Dropdown,
  DropdownItemProps,
  DropdownProps,
  Form,
  Icon,
  Message
} from 'semantic-ui-react';
import { I18n } from 'react-redux-i18n';
import SystemOnlyBadge from '../common/SystemOnlyBadge';
import Styles from './autocomplete.module.css';

export interface AutocompleteOption {
  /** Right-aligned details, one per line. */
  aside?: ReactNode[];
  disabled?: boolean;
  /** Extra lines under the subtitle. */
  lines?: ReactNode[];
  subtext?: ReactNode;
  text?: string;
  value?: any;
  [key: string]: any;
}

export interface AutocompleteProps {
  /** Lets a typed value that matches no option be chosen. */
  allowFreeText?: boolean;
  className?: string;
  clear?: () => void;
  clearOnChange?: boolean;
  /** Dense layout for narrow spaces such as grid filters. */
  compact?: boolean;
  disabled?: boolean;
  id?: string;
  /** Accepted for older callers; not rendered. */
  inputLabel?: ReactNode;
  label?: ReactNode;
  loading?: boolean;
  /** Ref to the dropdown, whose clearValue() resets it. */
  myRef?: Ref<any>;
  name?: string;
  onChange?: (event: any, data: any) => void;
  onClear?: () => void;
  onFocus?: (event: any, data: DropdownProps) => void;
  options?: (AutocompleteOption | null | undefined)[];
  placeholder?: string;
  search?: (event: any, data: { searchQuery: string }) => void;
  searchQuery?: string;
  /** Row for the chosen value, shown when its option is not among the results. */
  selectedOption?: AutocompleteOption;
  subtext?: ReactNode;
  /** Marks a field only system-level credentials can see; the badge follows the label. */
  systemOnly?: boolean;
  validation?: { required?: boolean } | null;
  validationErrorMessage?: ReactNode;
  value?: any;
}

const OptionRow: React.FC<{
  compact?: boolean;
  option: AutocompleteOption;
}> = ({ compact, option }) => (
  <span className={Styles.option}>
    <span className={Styles.main}>
      <span className={Styles.title} title={compact ? option.text : undefined}>
        {option.text}
      </span>
      {option.subtext && (
        <span className={Styles.subtext}>{option.subtext}</span>
      )}
      {option.lines?.map((line, index) => (
        <span key={index} className={Styles.line}>
          {line}
        </span>
      ))}
    </span>
    {!!option.aside?.length && (
      <span className={Styles.aside}>
        {option.aside.map((detail, index) => (
          <span key={index}>{detail}</span>
        ))}
      </span>
    )}
  </span>
);

// Callers search server-side, so every option they pass is shown. A copy, as Semantic appends to it.
const showAll = (options: DropdownItemProps[]) => [...options];

const Autocomplete: React.FC<AutocompleteProps> = (props) => {
  const {
    allowFreeText,
    className,
    compact,
    disabled,
    id,
    label,
    loading,
    myRef,
    name,
    options = [],
    placeholder,
    searchQuery,
    subtext,
    systemOnly,
    validation,
    validationErrorMessage,
    value
  } = props;
  const ownRef = useRef<any>(null);
  const generatedId = useId();
  const inputId = `${id ?? generatedId}-input`;
  const labelId = `${id ?? generatedId}-label`;
  const [typedQuery, setTypedQuery] = useState('');
  const [awaitingResults, setAwaitingResults] = useState(false);
  const [editing, setEditing] = useState(false);
  const chosen = useRef<{ option: AutocompleteOption; value: any }>();

  const hasValue = value !== undefined && value !== null && value !== '';
  const results = options.filter((item): item is AutocompleteOption => !!item);
  const resultsKey = results.map((item) => String(item.value)).join('|');
  const chosenOption = hasValue
    ? results.find((item) => item.value === value)
    : undefined;
  if (chosenOption) {
    chosen.current = { option: chosenOption, value };
  }
  const remembered = chosen.current;
  const chosenRow: AutocompleteOption | undefined = hasValue
    ? (chosenOption ??
      (remembered && remembered.value === value
        ? remembered.option
        : undefined) ??
      props.selectedOption ?? { text: String(value), value })
    : undefined;
  // Forms show the chosen value as a summary; Change switches back to searching.
  const showSummary = !compact && hasValue && !editing;

  useEffect(() => {
    if (!loading) {
      setAwaitingResults(false);
    }
  }, [loading]);

  useEffect(() => {
    setAwaitingResults(false);
  }, [resultsKey]);

  useEffect(() => {
    if (editing) {
      document.getElementById(inputId)?.focus();
    }
  }, [editing]);

  // The list only shows results for text typed into this field right now, once a search for it
  // has finished.
  const shown = typedQuery && !awaitingResults ? [...results] : [];
  const chosenText =
    compact && hasValue && !shown.some((item) => item.value === value)
      ? chosenRow?.text
      : undefined;
  const dropdownOptions: DropdownItemProps[] = shown.map((item, index) => ({
    content: <OptionRow compact={compact} option={item} />,
    disabled: loading || item.disabled,
    key: item.value || index,
    text: item.text,
    value: item.value
  }));

  const clear = () => {
    setTypedQuery('');
    setEditing(false);
    if (value || searchQuery) {
      props.onChange?.({}, {});
      props.clear?.();
    }
    const dropdown =
      (myRef as { current?: any } | null)?.current || ownRef.current;
    dropdown?.clearValue?.();
    props.onClear?.();
  };

  const handleChange = (event: any, data: DropdownProps) => {
    if (!event) {
      return;
    }
    setTypedQuery('');
    setEditing(false);
    props.onChange?.(event, data);
    if (props.clearOnChange) {
      clear();
    }
  };

  const startChange = () => {
    setEditing(true);
    if (searchQuery) {
      props.search?.(undefined, { searchQuery: '' });
    }
  };

  const handleSearchChange = (event: any, data: DropdownProps) => {
    const query = data.searchQuery ?? '';
    setTypedQuery(query);
    setAwaitingResults(!!query);
    props.search?.(event, { ...data, searchQuery: query });
  };

  // Leaving drops the typed text. Fields that hold their own text are told through an empty search.
  const handleBlur = (event: any) => {
    setEditing(false);
    if (!typedQuery) {
      return;
    }
    setTypedQuery('');
    setAwaitingResults(false);
    if (searchQuery) {
      props.search?.(event, { searchQuery: '' });
    }
  };

  const handleFocus = (event: any, data: DropdownProps) => {
    if (props.onFocus) {
      props.onFocus(event, data);
      return;
    }
    event?.target?.setAttribute?.('autocomplete', 'nope');
  };

  const clearLabel =
    typeof label === 'string'
      ? I18n.t('common.autocomplete.clearLabel', { field: label })
      : undefined;
  const changeLabel =
    typeof label === 'string'
      ? I18n.t('common.autocomplete.changeLabel', { field: label })
      : undefined;
  const inlineClear = compact && !disabled && !loading && hasValue;
  const emptyMessage = I18n.t(
    !typedQuery
      ? 'common.autocomplete.typeToSearch'
      : loading || awaitingResults
        ? 'common.autocomplete.searching'
        : 'common.autocomplete.noMatches'
  );
  const fieldClassName = [className, compact && Styles.compact]
    .filter(Boolean)
    .join(' ');

  const dropdown = (
    <Dropdown
      id={id}
      name={name}
      ref={myRef || ownRef}
      className={
        compact && hasValue
          ? `${Styles.dropdown} ${Styles.hasValue}`
          : Styles.dropdown
      }
      selection
      search={showAll}
      searchInput={{ id: inputId }}
      onSearchChange={handleSearchChange}
      loading={loading}
      noResultsMessage={emptyMessage}
      selectOnBlur={false}
      selectOnNavigation={false}
      onBlur={handleBlur}
      placeholder={placeholder}
      options={dropdownOptions}
      searchQuery={searchQuery}
      text={chosenText}
      disabled={disabled}
      value={!compact && editing ? '' : value}
      onFocus={handleFocus}
      onChange={handleChange}
      icon={inlineClear ? null : 'chevron down'}
      allowAdditions={allowFreeText}
      additionLabel={
        allowFreeText ? `${I18n.t('common.autocomplete.use')} ` : undefined
      }
    />
  );

  return (
    <Form.Field className={fieldClassName} required={!!validation?.required}>
      {(label || !compact) && (
        <div className={Styles.header}>
          {/* The badge stays outside the label: it is focusable, and the label names the input. */}
          {label && (
            <label id={labelId} htmlFor={showSummary ? undefined : inputId}>
              {label}
            </label>
          )}
          {label && systemOnly && <SystemOnlyBadge />}
          {!disabled && !compact && (
            <span className={Styles.actions}>
              {showSummary && (
                <button
                  type="button"
                  className={`Links ${Styles.clear}`}
                  aria-label={changeLabel}
                  onClick={startChange}
                >
                  {I18n.t('common.autocomplete.change')}
                </button>
              )}
              <button
                type="button"
                className={`Links ${Styles.clear}`}
                aria-label={clearLabel}
                onClick={clear}
              >
                {I18n.t('common.clear')}
              </button>
            </span>
          )}
        </div>
      )}
      {compact ? (
        <div className={Styles.control}>
          {dropdown}
          {inlineClear && (
            <button
              type="button"
              className={Styles.inlineClear}
              aria-label={clearLabel ?? I18n.t('common.clear')}
              onClick={clear}
            >
              <Icon name="close" fitted />
            </button>
          )}
        </div>
      ) : showSummary && chosenRow ? (
        <div
          id={id}
          className={Styles.summary}
          role="group"
          aria-labelledby={label ? labelId : undefined}
        >
          <OptionRow option={chosenRow} />
        </div>
      ) : (
        dropdown
      )}
      {subtext && <div className="input-sub">{subtext}</div>}
      <Message negative hidden={!validationErrorMessage}>
        <p>{validationErrorMessage}</p>
      </Message>
    </Form.Field>
  );
};

export default Autocomplete;
